'use client'

import { useEffect, useState } from 'react'
import { usePathname } from 'next/navigation'
import { ArrowLeft, ArrowRight, Check, ChevronRight, Gift, Palette, UserPlus, Wallet, X } from 'lucide-react'
import Input from '@/components/elements/Input'
import Textarea from '@/components/elements/Textarea'
import { DateTimePicker } from '@/components/elements/DateTimePicker'
import { useGiftCardForm } from '@/lib/commerce/hooks'
import { formatImageURL, formatPrice } from '@/utils/util'
import { GIFT_CARD_MIN_AMOUNT, GIFT_CARD_MAX_AMOUNT, GIFT_CARD_PRESETS } from '@/utils/giftCard'
import { navigateUrl } from '@/utils/client-request'

const STEPS = [
  { label: 'Дизайн' },
  { label: 'Үнийн дүн' },
  { label: 'Хүлээн авагч' },
]

type HeroDesign = {
  id: string
  sub: string
  bg: string
  fg: string
  accent: string
  art: 'asterisk' | 'balloons' | 'hearts'
  border?: string
}

const HERO_DESIGNS: HeroDesign[] = [
  { id: 'classic', sub: 'Shop.mn', bg: 'linear-gradient(135deg, #2B7FFF 0%, #7C86FF 100%)', fg: '#fff', accent: 'rgba(255,255,255,0.18)', art: 'asterisk' },
  { id: 'birthday', sub: 'Happy Birthday', bg: 'linear-gradient(135deg, #FF7A59 0%, #FFB36B 60%, #FFD27D 100%)', fg: '#231a14', accent: 'rgba(35,26,20,0.10)', art: 'balloons' },
  { id: 'love', sub: 'With love', bg: 'linear-gradient(135deg, #BE123C 0%, #F43F5E 100%)', fg: '#fff', accent: 'rgba(255,255,255,0.18)', art: 'hearts' },
]

export default function GiftCard({ templates, vendorData }: { templates: any, vendorData: any }) {
  const pathname = usePathname() || ''
  const f = useGiftCardForm({ templates, vendorData, totalSteps: STEPS.length })
  const [modalOpen, setModalOpen] = useState(false)

  const vendorName = vendorData?.name || 'Shop.mn'
  const vendorTemplates = (templates || []).filter((t: any) => t.vendorId)
  const globalTemplates = (templates || []).filter((t: any) => !t.vendorId)

  const openWithTemplate = (templateId: string) => {
    f.setGiftCardTemplateId(templateId)
    f.setStep(1)
    setModalOpen(true)
  }

  return (
    <section className="bg-cream pb-20 min-h-screen">
      <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10 pt-6">
        <nav className="text-[13px] text-foreground/55 mb-6 flex items-center gap-1.5 flex-wrap">
          <a href={navigateUrl('/', pathname)} className="hover:text-foreground">Нүүр</a>
          <ChevronRight className="size-3 opacity-50" />
          <span className="text-foreground font-medium">Бэлгийн карт</span>
        </nav>
      </div>

      {/* Hero */}
      <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          <div className="lg:col-span-6">
            <h1 className="mt-5 text-5xl sm:text-6xl font-bold font-gilroy tracking-[-0.025em] leading-[0.98] text-foreground">
              Хэн нэгний өдрийг<br /><span className="text-gradient">гэрэлтүүлэхэд</span><br />нэг карт хангалттай.
            </h1>
            <p className="mt-6 text-[15px] sm:text-[16px] leading-[1.6] text-foreground/65 max-w-xl">
              {vendorName} дэлгүүрийн бэлгийн картыг хайртай хүндээ илгээгээрэй. Та өөрийн хүссэн дизайн, үнийн дүнгээр илгээх боломжтой.
            </p>
          </div>

          <div className="lg:col-span-6">
            <div className="relative mx-auto" style={{ maxWidth: 480, minHeight: 340 }}>
              <div className="absolute top-9 -left-5 w-[70%] rotate-[-9deg] opacity-90">
                <IllustratedCard design={HERO_DESIGNS[1]} amount={50000} />
              </div>
              <div className="absolute top-5 -right-3 w-[78%] rotate-[7deg] opacity-95">
                <IllustratedCard design={HERO_DESIGNS[2]} amount={200000} />
              </div>
              <div className="relative pt-17.5 mx-auto" style={{ width: '92%' }}>
                <IllustratedCard design={HERO_DESIGNS[0]} amount={100000} maskCode />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Designs grid */}
      <div id="designs" className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10 mt-6">
        <div className="flex items-end justify-between mb-7 gap-4">
          <div>
            <h2 className="font-bold font-gilroy tracking-[-0.025em] leading-none text-foreground" style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)' }}>
              Дизайн сонгох
            </h2>
            <p className="mt-2 text-[13.5px] text-foreground/55">{(templates || []).length} өөр загвар</p>
          </div>
        </div>

        {vendorTemplates.length > 0 && (
          <div className="mb-10">
            <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-foreground/55 mb-4">{vendorName}-н дизайн</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-5 gap-y-8">
              {vendorTemplates.map((t: any) => (
                <DesignTile key={t.id} template={t} vendorName={vendorName} onSelect={() => openWithTemplate(t.id)} />
              ))}
            </div>
          </div>
        )}

        {globalTemplates.length > 0 && (
          <div>
            {vendorTemplates.length > 0 && (
              <div className="text-[11px] font-semibold tracking-[0.18em] uppercase text-foreground/55 mb-4">Бусад дизайн</div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-5 gap-y-8">
              {globalTemplates.map((t: any) => (
                <DesignTile key={t.id} template={t} vendorName={vendorName} onSelect={() => openWithTemplate(t.id)} />
              ))}
            </div>
          </div>
        )}
      </div>

      <div className="mx-auto max-w-[1280px] px-4 sm:px-6 lg:px-10 mt-20">
        <div className="mb-8">
          <h2 className="font-bold font-gilroy tracking-[-0.025em] leading-none text-foreground" style={{ fontSize: 'clamp(1.75rem, 3.5vw, 2.75rem)' }}>
            Хэрхэн ажилладаг вэ?
          </h2>
          <p className="mt-2 text-[13.5px] text-foreground/55">Гурван энгийн алхамаар бэлгийн картаа илгээгээрэй.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {[
            { n: '01', icon: Palette, title: 'Дизайн сонгох', desc: 'Хүлээн авагчдаа тохирох сэтгэгдлээ илэрхийлсэн загвараа сонгоно.' },
            { n: '02', icon: Wallet, title: 'Үнэ болон мэдээлэл', desc: `${formatPrice(GIFT_CARD_MIN_AMOUNT)}-${formatPrice(GIFT_CARD_MAX_AMOUNT)} хооронд дурын дүн оруулна.` },
            { n: '03', icon: UserPlus, title: 'Илгээх', desc: 'Хүлээн авагчийн и-мэйл, хүлээн авах огноог оруулж бэлгээ илгээнэ.' },
          ].map(({ n, icon: Icon, title, desc }) => (
            <div key={n} className="rounded-2xl bg-[#F1EEE7] p-6">
              <div className="flex items-center justify-between mb-5">
                <span className="text-[11px] font-semibold tracking-[0.18em] uppercase text-foreground/55">{n}</span>
                <div className="size-10 rounded-full bg-foreground text-cream inline-flex items-center justify-center">
                  <Icon className="size-4" />
                </div>
              </div>
              <h3 className="font-bold font-gilroy text-foreground text-[20px] tracking-[-0.015em] leading-tight">{title}</h3>
              <p className="mt-2 text-[13.5px] text-foreground/60 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
      </div>

      <GiftModal open={modalOpen} onClose={() => setModalOpen(false)} f={f} templates={templates || []} vendorName={vendorName} vendorData={vendorData} />
    </section>
  )
}

function DesignTile({ template, vendorName, onSelect }: { template: any, vendorName: string, onSelect: () => void }) {
  const thumbnail = template?.thumbnail ? formatImageURL(template.thumbnail) : null
  const subtitle = template?.vendorId ? vendorName : 'Shop.mn'
  return (
    <button onClick={onSelect} className="group text-left cursor-pointer">
      <div className="relative aspect-3/2 w-full overflow-hidden rounded-2xl bg-foreground transition-transform duration-300 group-hover:-translate-y-1">
        {thumbnail && <img src={thumbnail} alt={template?.name || ''} className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute bottom-3 right-3 size-9 rounded-full bg-foreground text-cream inline-flex items-center justify-center transition-transform group-hover:scale-110 shadow-lg">
          <ArrowRight className="size-4" />
        </div>
      </div>
      <div className="mt-3.5 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[14px] font-semibold text-foreground leading-snug truncate">{template.name}</div>
          <div className="text-[12px] text-foreground/55 leading-snug truncate">{subtitle}</div>
        </div>
        <div className="text-[12px] font-semibold text-foreground tabular-nums shrink-0">{formatPrice(5000)} - {formatPrice(500000)}</div>
      </div>
    </button>
  )
}

function CardArt({ kind, color }: { kind: HeroDesign['art'], color: string }) {
  if (kind === 'asterisk') {
    return (
      <svg viewBox="0 0 200 140" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
        <g opacity="0.18" fill={color}>
          <g transform="translate(160 18)">
            <rect x="-2.5" y="-22" width="5" height="44" rx="2.5" />
            <rect x="-2.5" y="-22" width="5" height="44" rx="2.5" transform="rotate(60)" />
            <rect x="-2.5" y="-22" width="5" height="44" rx="2.5" transform="rotate(120)" />
          </g>
        </g>
        <g opacity="0.10" fill={color}>
          <circle cx="20" cy="120" r="36" />
          <circle cx="180" cy="130" r="44" />
        </g>
      </svg>
    )
  }
  if (kind === 'balloons') {
    return (
      <svg viewBox="0 0 200 140" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
        <g opacity="0.85">
          <ellipse cx="158" cy="34" rx="13" ry="16" fill="#fff" />
          <ellipse cx="174" cy="42" rx="11" ry="13.5" fill="#FFD27D" />
          <ellipse cx="166" cy="58" rx="10" ry="12" fill="#FF7A59" />
          <path d="M158 50 Q160 75 152 95 M174 56 Q174 82 168 100 M166 70 Q170 90 160 110" stroke={color} strokeWidth="0.6" fill="none" opacity="0.5" />
        </g>
        <g opacity="0.18" fill={color}>
          <circle cx="20" cy="125" r="40" />
        </g>
      </svg>
    )
  }
  if (kind === 'hearts') {
    return (
      <svg viewBox="0 0 200 140" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid slice">
        <g fill={color} opacity="0.95">
          <path transform="translate(160 38) scale(1.3)" d="M0 8 C-8 -2 -16 -2 -16 -8 C-16 -14 -8 -14 0 -6 C8 -14 16 -14 16 -8 C16 -2 8 -2 0 8 Z" />
        </g>
        <g fill={color} opacity="0.25">
          <path transform="translate(40 96) scale(0.7)" d="M0 8 C-8 -2 -16 -2 -16 -8 C-16 -14 -8 -14 0 -6 C8 -14 16 -14 16 -8 C16 -2 8 -2 0 8 Z" />
          <path transform="translate(72 30) scale(0.5)" d="M0 8 C-8 -2 -16 -2 -16 -8 C-16 -14 -8 -14 0 -6 C8 -14 16 -14 16 -8 C16 -2 8 -2 0 8 Z" />
        </g>
      </svg>
    )
  }
  return null
}

function IllustratedCard({ design, amount, maskCode }: { design: HeroDesign, amount: number, maskCode?: boolean }) {
  const isLight = design.fg !== '#fff'
  return (
    <div
      className="relative aspect-3/2 w-full rounded-2xl overflow-hidden"
      style={{ background: design.bg, color: design.fg, border: design.border, boxShadow: '0 12px 28px -10px rgba(15,23,42,0.30), 0 2px 6px -2px rgba(15,23,42,0.10)' }}
    >
      <CardArt kind={design.art} color={design.fg} />
      <div className="absolute inset-0 pointer-events-none" style={{ background: 'linear-gradient(115deg, rgba(255,255,255,0) 35%, rgba(255,255,255,0.22) 50%, rgba(255,255,255,0) 65%)' }} />
      <div className="absolute inset-0 flex flex-col justify-between p-5 sm:p-6">
        <div className="flex items-start justify-between">
          <div className="leading-tight">
            <div className="text-[10px] font-semibold tracking-[0.22em] uppercase opacity-80">Бэлгийн карт</div>
            <div className="text-[14px] font-bold tracking-[-0.005em] mt-1 font-gilroy">Shop.mn</div>
          </div>
          <div
            className="relative"
            style={{ width: 28, height: 22, borderRadius: 4, background: isLight ? 'rgba(35,26,20,0.85)' : 'rgba(255,255,255,0.9)' }}
          >
            <div className="absolute inset-0.75 rounded-sm" style={{ border: `0.8px solid ${isLight ? 'rgba(255,255,255,0.5)' : 'rgba(0,0,0,0.25)'}` }} />
          </div>
        </div>
        <div className="flex items-end justify-between">
          <div>
            <div className="font-bold font-gilroy tracking-[-0.025em] leading-none tabular-nums" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.25rem)' }}>
              {formatPrice(amount)}
            </div>
            <div className="font-medium mt-1.5" style={{ fontSize: 11, opacity: 0.75, letterSpacing: 0.4 }}>{design.sub}</div>
          </div>
          {maskCode && (
            <div className="text-[10px] font-mono tracking-[0.18em] px-2 py-1 rounded-md" style={{ background: design.accent }}>•••• ••••</div>
          )}
        </div>
      </div>
    </div>
  )
}

function PreviewCard({ template, amount, recipient, message, vendorName, vendorData, maskCode }: { template: any, amount: number, recipient?: string, message?: string, vendorName: string, vendorData?: any, maskCode?: boolean }) {
  const thumbnail = template?.thumbnail ? formatImageURL(template.thumbnail) : null
  return (
    <div className="relative aspect-3/2 w-full rounded-2xl overflow-hidden bg-foreground text-white">
      {thumbnail && <img src={thumbnail} alt={template?.name || ''} className="absolute inset-0 w-full h-full object-cover" />}
      <div className="absolute inset-0 bg-linear-to-br from-black/15 to-black/45" />
      <div className="absolute inset-0 flex flex-col p-5 sm:p-6">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2.5 min-w-0">
            {vendorData?.logo && (
              <img src={formatImageURL(vendorData.logo)} alt={vendorName} className="size-9 rounded-lg object-cover bg-white/90 p-0.5 shrink-0" />
            )}
            <div className="min-w-0">
              <div className="text-[10px] font-semibold tracking-[0.22em] uppercase opacity-80">Бэлгийн карт</div>
              <div className="text-[14px] font-bold tracking-[-0.005em] mt-1 font-gilroy truncate">{vendorName}</div>
            </div>
          </div>
          {template?.name && (
            <div className="text-[10px] font-semibold tracking-[0.18em] uppercase opacity-80 shrink-0">{template.name}</div>
          )}
        </div>
        <div className="flex-1 flex items-start min-w-0 pt-2">
          {message ? (
            <div className="text-[12px] opacity-90 italic line-clamp-2 leading-relaxed">"{message}"</div>
          ) : maskCode ? (
            <div className="text-[12px] opacity-75 font-mono tracking-[0.18em]">•••• ••••</div>
          ) : null}
        </div>
        <div className="flex justify-end">
          <div className="text-right">
            <div className="font-bold font-gilroy tracking-[-0.025em] leading-none tabular-nums" style={{ fontSize: 'clamp(1.5rem, 3vw, 2.25rem)' }}>{formatPrice(amount)}</div>
            {recipient && <div className="text-[12px] opacity-85 mt-1.5">{recipient}-д</div>}
          </div>
        </div>
      </div>
    </div>
  )
}

function GiftModal({ open, onClose, f, templates, vendorName, vendorData }: { open: boolean, onClose: () => void, f: any, templates: any[], vendorName: string, vendorData?: any }) {
  useEffect(() => {
    if (!open) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [open])

  if (!open) return null

  const amountInt = f.amount && parseInt(f.amount, 10) > 0 ? parseInt(f.amount, 10) : 0

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4 bg-foreground/55 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white w-full sm:max-w-130 max-h-[94vh] sm:max-h-[88vh] rounded-t-3xl sm:rounded-3xl shadow-2xl flex flex-col overflow-hidden"
      >
        <div className="flex items-center justify-between px-5 pt-5 pb-3 shrink-0">
          <button
            onClick={() => (f.step === 0 ? onClose() : f.back())}
            className="size-9 inline-flex items-center justify-center rounded-full hover:bg-[#F1EEE7] text-foreground cursor-pointer"
            aria-label="Буцах"
          >
            <ArrowLeft className="size-4" />
          </button>
          <div className="flex items-center gap-1.5">
            {STEPS.map((_, i) => (
              <div
                key={i}
                className="h-1 rounded-full transition-all"
                style={{ width: i === f.step ? 22 : 8, background: i === f.step ? 'var(--foreground)' : 'rgba(0,0,0,0.12)' }}
              />
            ))}
          </div>
          <button
            onClick={onClose}
            className="size-9 inline-flex items-center justify-center rounded-full hover:bg-[#F1EEE7] text-foreground cursor-pointer"
            aria-label="Хаах"
          >
            <X className="size-4" />
          </button>
        </div>

        <div className="px-5 sm:px-6 pt-1 pb-3 shrink-0">
          <PreviewCard template={f.selectedTemplate} amount={amountInt} recipient={f.fullName} message={f.message} vendorName={vendorName} vendorData={vendorData} />
        </div>

        <div className="flex-1 overflow-y-auto px-5 sm:px-6 pb-2">
          {f.step === 0 && (
            <div>
              <h3 className="text-[18px] font-bold font-gilroy text-foreground mb-1">Картын дизайн</h3>
              <p className="text-[13px] text-foreground/55 mb-4">Дээрх жагсаалтаас сонгох эсвэл доороос өөр загвар сонгоно.</p>
              <div className="grid grid-cols-3 gap-2.5">
                {templates.map((t: any) => (
                  <button
                    key={t.id}
                    type="button"
                    onClick={() => f.setGiftCardTemplateId(t.id)}
                    className={`group flex flex-col gap-1.5 text-left cursor-pointer ${f.giftCardTemplateId === t.id ? '' : 'opacity-90 hover:opacity-100'}`}
                  >
                    <div className={`aspect-3/2 w-full overflow-hidden rounded-lg transition-all ${f.giftCardTemplateId === t.id ? 'ring-2 ring-foreground ring-offset-2 ring-offset-white' : 'ring-0'}`}>
                      <img src={formatImageURL(t.thumbnail)} alt={t.name} className="w-full h-full object-cover" />
                    </div>
                    <div className={`text-[11.5px] text-center font-medium truncate ${f.giftCardTemplateId === t.id ? 'text-foreground' : 'text-foreground/55'}`}>{t.name}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {f.step === 1 && (
            <div>
              <h3 className="text-[18px] font-bold font-gilroy text-foreground mb-1">Үнийн дүнг тохируулна уу</h3>
              <p className="text-[13px] text-foreground/55 mb-4">{formatPrice(GIFT_CARD_MIN_AMOUNT)}-аас {formatPrice(GIFT_CARD_MAX_AMOUNT)}-н хооронд.</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-3">
                {GIFT_CARD_PRESETS.map((p: number) => {
                  const selected = f.amount === String(p)
                  return (
                    <button
                      key={p}
                      onClick={() => f.setAmount(String(p))}
                      className={`h-12 rounded-xl text-[13.5px] font-semibold transition-colors cursor-pointer ${selected ? 'bg-foreground text-cream' : 'bg-[#F1EEE7] text-foreground hover:bg-[#E9E4DA]'}`}
                    >
                      {formatPrice(p)}
                    </button>
                  )
                })}
              </div>
              <Input placeholder={`Та ${formatPrice(GIFT_CARD_MIN_AMOUNT)}-${formatPrice(GIFT_CARD_MAX_AMOUNT)} үнийн дүнг оруулах боломжтой`} value={f.amount} onChange={(e: any) => f.setAmount(e.target.value)} type="number" description="Гараар мөнгөн дүнг тохируулах" />
            </div>
          )}

          {f.step === 2 && (
            <div className="flex flex-col gap-4">
              <div>
                <h3 className="text-[18px] font-bold font-gilroy text-foreground mb-1">Хүлээн авагчийн мэдээлэл</h3>
                <p className="text-[13px] text-foreground/55">Картыг өгөгдсөн огноонд автоматаар имэйлээр илгээнэ.</p>
              </div>
              {f.isGuest && (
                <Input label="Өөрийн и-мэйл" placeholder="me@mail.mn" value={f.senderEmail} onChange={(e: any) => f.setSenderEmail(e.target.value || f.session?.user?.email || '')} required />
              )}
              <Input label="Хүлээн авагчийн нэр" placeholder="Хүлээн авагчийн нэр" value={f.fullName} onChange={(e: any) => f.setFullName(e.target.value)} required />
              <Input label="Хүлээн авагчийн и-мэйл" placeholder="example@mail.mn" value={f.email} onChange={(e: any) => f.setEmail(e.target.value)} required />
              <Textarea value={f.message} onChange={(e: any) => f.setMessage(e.target.value)} label="Зурвас" placeholder="Сайн уу.." />
              <DateTimePicker date={f.deliveryDate} setDate={(d: Date | undefined) => f.setDeliveryDate(d || null)} label="Илгээх огноо" className="w-full!" />
              <label className="flex items-start gap-2.5 text-[13px] text-foreground/70 cursor-pointer">
                <input type="checkbox" checked={f.sendCopy} onChange={(e) => f.setSendCopy(e.target.checked)} className="mt-0.5 size-4 rounded border-foreground/20 accent-foreground" />
                <span>Бэлгийн картын хуулбарыг миний имэйлд илгээх</span>
              </label>
            </div>
          )}
        </div>

        <div className="px-5 sm:px-6 py-4 border-t border-foreground/8 flex items-center justify-between gap-3 shrink-0">
          <div className="text-[12px] text-foreground/55">
            {f.amount ? <>Нийт <span className="font-semibold text-foreground tabular-nums">{formatPrice(amountInt)}</span></> : 'Дүнгээ сонгоно уу'}
          </div>
          {f.step < STEPS.length - 1 ? (
            <button onClick={f.next} className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-foreground text-cream text-[13.5px] font-semibold hover:bg-foreground/90 transition-colors cursor-pointer">
              Үргэлжлүүлэх <ArrowRight className="size-4" />
            </button>
          ) : (
            <button onClick={f.submit} disabled={f.loading} className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-foreground text-cream text-[13.5px] font-semibold hover:bg-foreground/90 disabled:opacity-60 transition-colors cursor-pointer">
              <Gift className="size-4" /> {f.loading ? 'Захиалж байна...' : 'Захиалах'}
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
